import { useMemo } from 'react';
import moment from 'moment-timezone';
import {
  TIME_OPTIONS,
  parse12hrTime,
  formatTime12,
  findClosestTimeOption,
} from '../../fields/dateTime';

interface UseTimeOptionsArgs {
  selectedStartDate: Date | null;
  selectedStartTime: string;
  /** Currently selected end time (typed/picked) — injected so an
   *  off-grid value always renders, symmetric to start. */
  selectedEndTime?: string;
  calendarBookedStartIso: string | null;
  /**
   * ISO of the booked slot's `end_time`. When the actual end falls
   * between two 15-min grid slots (e.g. 6-min interview ending at
   * 10:06), inject that exact time into `endTimeOptions` so the
   * `<Select>` can render it as the chosen value. Without this the
   * picker either auto-corrects away from the recruiter's value or we
   * have to display the next 15-min ceiling, which makes a 6-min
   * interview look like 15.
   */
  calendarBookedEndIso?: string | null;
  scheduleTz: string;
}

export interface TimeOptionsResult {
  /** Filtered start-time options — past times hidden when date is today. */
  startTimeOptions: typeof TIME_OPTIONS;
  /** Filtered end-time options — only times after the selected start.
   *  Includes the actual `bookedSlot.end` (time-of-day) when it falls
   *  off the 15-min grid, so the Select can render the recruiter's
   *  exact end without auto-correcting. */
  endTimeOptions: typeof TIME_OPTIONS;
  /** Minimum selectable date for the date picker. Advances to tomorrow
   *  when the selected start time is at or before the current wall clock. */
  datePickerMinDate: Date;
  /** The resolved effective start date (form value ?? calendar hint). */
  effectiveStartDate: Date | null;
  /** The resolved effective start time string (form value ?? calendar hint). */
  effectiveStartTimeStr: string;
}

export function useTimeOptions({
  selectedStartDate,
  selectedStartTime,
  selectedEndTime = '',
  calendarBookedStartIso,
  calendarBookedEndIso,
  scheduleTz,
}: UseTimeOptionsArgs): TimeOptionsResult {
  const calendarHintTime12 =
    calendarBookedStartIso !== null && calendarBookedStartIso !== ''
      ? findClosestTimeOption(formatTime12(calendarBookedStartIso, scheduleTz))
      : '';

  const effectiveStartTimeStr = selectedStartTime || calendarHintTime12 || '';

  const effectiveStartDate: Date | null =
    selectedStartDate ??
    (calendarBookedStartIso ? new Date(calendarBookedStartIso) : null);

  const isSelectedDateToday = effectiveStartDate
    ? moment(effectiveStartDate)
        .tz(scheduleTz)
        .isSame(moment().tz(scheduleTz), 'day')
    : false;

  const nowMins =
    moment().tz(scheduleTz).hours() * 60 + moment().tz(scheduleTz).minutes();

  const startTimeParsedForMinDate = effectiveStartTimeStr
    ? parse12hrTime(effectiveStartTimeStr)
    : null;
  const selectedTimeMins = startTimeParsedForMinDate
    ? startTimeParsedForMinDate.hours * 60 + startTimeParsedForMinDate.minutes
    : -1;
  const isWallClockStartTimePastOrEqualNow =
    selectedTimeMins >= 0 && selectedTimeMins <= nowMins;

  const datePickerMinDate = useMemo(
    () =>
      isWallClockStartTimePastOrEqualNow
        ? moment().tz(scheduleTz).add(1, 'day').startOf('day').toDate()
        : moment().tz(scheduleTz).startOf('day').toDate(),
    [isWallClockStartTimePastOrEqualNow, scheduleTz]
  );

  const baseStartTimeOptions = isSelectedDateToday
    ? TIME_OPTIONS.filter((opt) => {
        const p = parse12hrTime(opt.value);
        return p ? p.hours * 60 + p.minutes > nowMins : false;
      })
    : TIME_OPTIONS;

  // The creatable Select can only DISPLAY a controlled value that
  // exists in `options`. The current start value can arrive from
  // several flows, each with a different live source:
  //   • typed odd time            → `selectedStartTime` (DetailsForm
  //                                  state, set via handleFieldChange)
  //   • drag / resize / click /   → `calendarBookedStartIso`
  //     edit-mount hydration         (sharedState.bookedSlot.start)
  // `selectedStartTime` is NOT updated on programmatic setValue (the
  // reverse calendar→form sync), so preferring it dropped the dragged
  // value and the Select went blank. Inject BOTH candidates (off-grid,
  // deduped) so whatever the field currently holds is always present.
  const startTimeOptions = (() => {
    const candidates = [
      selectedStartTime,
      calendarBookedStartIso
        ? formatTime12(calendarBookedStartIso, scheduleTz)
        : '',
    ];
    const have = new Set(baseStartTimeOptions.map((o) => o.value));
    const extra: typeof baseStartTimeOptions = [];
    for (const v of candidates) {
      if (!v || !parse12hrTime(v) || have.has(v)) continue;
      have.add(v);
      extra.push({ value: v, label: v });
    }
    if (extra.length === 0) return baseStartTimeOptions;
    return [...baseStartTimeOptions, ...extra].sort((a, b) => {
      const pa = parse12hrTime(a.value);
      const pb = parse12hrTime(b.value);
      const ma = pa ? pa.hours * 60 + pa.minutes : 0;
      const mb = pb ? pb.hours * 60 + pb.minutes : 0;
      return ma - mb;
    });
  })();

  // End time offers the FULL grid — never filtered by start time. A
  // multi-day slot legitimately ends at an earlier clock time than it
  // starts (e.g. starts 3:15 PM, ends 2:45 PM next day), so filtering to
  // "after start" would hide valid options. end-after-start (as a full
  // datetime) is enforced by the schema `validate` + the sync instead.
  const baseEndTimeOptions = TIME_OPTIONS;

  // Symmetric to the start injection: the creatable Select can only
  // DISPLAY a controlled value present in `options`. The current end
  // arrives from typing (`selectedEndTime`) or drag / resize / click /
  // edit-mount (`calendarBookedEndIso`). Inject BOTH off-grid
  // candidates (deduped). The booked end is injected UNCONDITIONALLY —
  // the old `extraMins <= startTimeMins` early-return blanked the field
  // for short/odd-start interviews (startTimeMins came from a snapped/
  // stale hint); end-after-start is enforced by the schema `validate`
  // (visible error) instead of a confusing empty Select.
  const endTimeOptions = (() => {
    const candidates = [
      selectedEndTime,
      calendarBookedEndIso
        ? formatTime12(calendarBookedEndIso, scheduleTz)
        : '',
    ];
    const have = new Set(baseEndTimeOptions.map((o) => o.value));
    const extra: typeof baseEndTimeOptions = [];
    for (const v of candidates) {
      if (!v || !parse12hrTime(v) || have.has(v)) continue;
      have.add(v);
      extra.push({ value: v, label: v });
    }
    if (extra.length === 0) return baseEndTimeOptions;
    return [...baseEndTimeOptions, ...extra].sort((a, b) => {
      const pa = parse12hrTime(a.value);
      const pb = parse12hrTime(b.value);
      const ma = pa ? pa.hours * 60 + pa.minutes : 0;
      const mb = pb ? pb.hours * 60 + pb.minutes : 0;
      return ma - mb;
    });
  })();

  return {
    startTimeOptions,
    endTimeOptions,
    datePickerMinDate,
    effectiveStartDate,
    effectiveStartTimeStr,
  };
}
